import { useState } from 'react';
import {
  Button, Checkbox, Input, InputNumber, Select,
} from 'antd';
import * as TablerIcons from '@tabler/icons-react';
import LabelledRow from '../LabelledRow';
import BoxColorField from './BoxColorField';
import BoxBorderFields from './BoxBorderFields';
import BoxImageField from './BoxImageField';
import FieldGroupLabel from './FieldGroupLabel';
import { cssLengthToPx, pxToRemCss } from '../../../shared/boxes/cssLength';
import { INFO_CARD_TITLE_STYLE_OPTIONS, infoCardRatingSizePx } from '../../../shared/boxes/infoCard/infoCardRender';
import { SHADOW_LABEL, SHADOW_OPTIONS } from '../../../shared/boxes/shadowStyles';
import { INFO_CARD_GROUPS, INFO_CARD_LABELS } from '../../../shared/boxes/infoCard/infoCardLabels';
import { searchIcons } from '../../../webviewer/puck/fields/iconCatalog';

// Largeurs des contrôles posés en bout de ligne. Le libellé prend ce qui
// reste : une liste déroulante pleine largeur ne laissait pas la place de
// l'écrire à côté, d'où l'empilement d'avant.
const SELECT_WIDTH = 116;
const NUMBER_WIDTH = 72;

const ILLUSTRATION_OPTIONS = [
  { value: 'icon', label: 'Icône' },
  { value: 'image', label: 'Photo' },
  { value: 'none', label: 'Aucune' },
];

const CONTENT_ALIGN_OPTIONS = [
  { value: 'left', label: 'Gauche' },
  { value: 'center', label: 'Centre' },
  { value: 'right', label: 'Droite' },
];

/** Sélecteur d'icône : mêmes mots-clés français que la galerie du site. */
const IconSelect = ({ value, disabled, onChange }) => {
  const [query, setQuery] = useState('');
  const names = searchIcons(query);
  return (
    <Select
      showSearch
      disabled={disabled}
      value={value || undefined}
      placeholder="Choisir une icône"
      onSearch={setQuery}
      onChange={onChange}
      filterOption={false}
      style={{ width: '100%' }}
      options={names.slice(0, 40).map((name) => {
        const Icon = TablerIcons[name];
        return {
          value: name,
          label: (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              {Icon ? <Icon size={16} /> : null}
              {name.replace(/^Icon/, '')}
            </span>
          ),
        };
      })}
    />
  );
};

/** Badges : libellé + couleur, ajoutés et retirés à la volée. */
const BadgesField = ({ badges, disabled, labelOfColor, colorOfLabel, onChange }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
    {badges.map((badge, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <div key={index} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <Input
          size="small"
          value={badge.label}
          disabled={disabled}
          placeholder="Libellé"
          style={{ flex: 1, minWidth: 0 }}
          onChange={(e) => onChange(badges.map((b, i) => (
            i === index ? { ...b, label: e.target.value } : b
          )))}
        />
        <BoxColorField
          label="Couleur du badge"
          disabled={disabled}
          value={labelOfColor(badge.color)}
          onChange={(label) => onChange(badges.map((b, i) => (
            i === index ? { ...b, color: colorOfLabel(label) } : b
          )))}
        />
        <Button
          danger
          size="small"
          disabled={disabled}
          title="Retirer ce badge"
          onClick={() => onChange(badges.filter((_, i) => i !== index))}
        >
          ×
        </Button>
      </div>
    ))}
    <Button
      size="small"
      disabled={disabled}
      onClick={() => onChange([...badges, { label: 'Badge', color: '' }])}
    >
      Ajouter un badge
    </Button>
  </div>
);

/**
 * Réglages d'une Carte info — servis à l'identique dans le configurateur du
 * template (où ils décrivent un modèle) et dans le panel de droite d'une box
 * (où ils personnalisent cette box).
 *
 * Un réglage n'existe QUE sur la box : la photo, qui s'importe depuis
 * l'ordinateur comme n'importe quelle image du document — un modèle de
 * template n'a pas à figer une photo. Le logo d'entreprise, lui, reste
 * web-only : il n'a pas d'usage dans un document.
 *
 * Une ligne par réglage, libellé à gauche et contrôle à droite (LabelledRow,
 * la ligne des autres panneaux de box), regroupées sous des intitulés. Chaque
 * réglage occupait auparavant deux lignes, son libellé au-dessus d'une liste
 * pleine largeur : la carte en compte une quinzaine, il fallait dérouler trois
 * écrans pour atteindre les bordures. Les intitulés de groupe portent le
 * contexte que les libellés longs portaient seuls (« Couleur de
 * l'illustration » → « Couleur », sous « Illustration »).
 */
const InfoCardFields = ({
  style = {}, locked, box, setProp, labelOfColor, colorOfLabel,
}) => {
  const badges = Array.isArray(style.badges) ? style.badges : [];
  const isBox = !!box;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <FieldGroupLabel>{INFO_CARD_GROUPS.illustration}</FieldGroupLabel>
      <LabelledRow label="Type">
        <Select
          size="small"
          disabled={locked}
          value={style.illustration}
          options={ILLUSTRATION_OPTIONS}
          onChange={(value) => setProp('illustration', value)}
          style={{ width: SELECT_WIDTH }}
        />
      </LabelledRow>
      {/* Icône et photo : trop larges pour tenir en bout de ligne, le libellé
          passe au-dessus. */}
      {style.illustration === 'icon' && (
        <LabelledRow label="Icône" stacked>
          <IconSelect
            value={style.icon}
            disabled={locked}
            onChange={(value) => setProp('icon', value)}
          />
        </LabelledRow>
      )}
      {style.illustration === 'image' && isBox && (
        <LabelledRow label="Photo" stacked>
          <BoxImageField
            value={style.imageUrl}
            disabled={locked}
            mediaId={style.imageMediaId}
            resolution={style.imageResolution}
            onChange={(url) => setProp('imageUrl', url)}
            onMediaChange={({ url, mediaId, resolution }) => {
              setProp('imageUrl', url);
              setProp('imageMediaId', mediaId ?? null);
              setProp('imageResolution', resolution ?? null);
            }}
          />
        </LabelledRow>
      )}
      {style.illustration !== 'none' && (
        <>
          <LabelledRow label="Forme">
            <Select
              size="small"
              disabled={locked}
              value={style.illustrationShape}
              options={[
                { value: 'round', label: 'Ronde' },
                { value: 'square', label: 'Carrée' },
              ]}
              onChange={(value) => setProp('illustrationShape', value)}
              style={{ width: SELECT_WIDTH }}
            />
          </LabelledRow>
          <LabelledRow label="Taille">
            <InputNumber
              size="small"
              min={16}
              max={200}
              disabled={locked}
              style={{ width: NUMBER_WIDTH }}
              value={cssLengthToPx(style.illustrationSize) ?? 52}
              onChange={(px) => setProp('illustrationSize', pxToRemCss(px))}
            />
          </LabelledRow>
          <LabelledRow label="Couleur">
            <BoxColorField
              label="Couleur de l'illustration"
              disabled={locked}
              value={labelOfColor(style.color)}
              onChange={(label) => setProp('color', colorOfLabel(label))}
            />
          </LabelledRow>
        </>
      )}

      <FieldGroupLabel>{INFO_CARD_GROUPS.texts}</FieldGroupLabel>
      <LabelledRow label="Typographie">
        <Select
          size="small"
          disabled={locked}
          value={style.titleStyle}
          options={INFO_CARD_TITLE_STYLE_OPTIONS}
          onChange={(value) => setProp('titleStyle', value)}
          style={{ width: SELECT_WIDTH }}
        />
      </LabelledRow>
      {/* Couleurs de DÉPART des deux textes, propres au document. */}
      <LabelledRow label="Couleur du titre">
        <BoxColorField
          label="Couleur du titre"
          disabled={locked}
          value={labelOfColor(style.titleColor)}
          onChange={(label) => setProp('titleColor', colorOfLabel(label))}
        />
      </LabelledRow>
      <LabelledRow label="Couleur de la description">
        <BoxColorField
          label="Couleur de la description"
          disabled={locked}
          value={labelOfColor(style.descriptionColor)}
          onChange={(label) => setProp('descriptionColor', colorOfLabel(label))}
        />
      </LabelledRow>

      <FieldGroupLabel>{INFO_CARD_GROUPS.decor}</FieldGroupLabel>
      <LabelledRow label={INFO_CARD_LABELS.quoteMark}>
        <Checkbox
          checked={!!style.quoteMark}
          disabled={locked}
          onChange={(e) => setProp('quoteMark', e.target.checked)}
        />
      </LabelledRow>
      <LabelledRow label={INFO_CARD_LABELS.rating}>
        <InputNumber
          size="small"
          min={0}
          max={5}
          disabled={locked}
          style={{ width: NUMBER_WIDTH }}
          value={style.rating ?? 0}
          onChange={(value) => setProp('rating', value ?? 0)}
        />
      </LabelledRow>
      {(style.rating ?? 0) > 0 && (
        <>
          <LabelledRow label={INFO_CARD_LABELS.ratingColor}>
            {/* Sa propre couleur : teinter l'illustration ne doit pas teindre
                la note au passage. */}
            <BoxColorField
              label={INFO_CARD_LABELS.ratingColor}
              disabled={locked}
              value={labelOfColor(style.ratingColor)}
              onChange={(label) => setProp('ratingColor', colorOfLabel(label))}
            />
          </LabelledRow>
          <LabelledRow label={INFO_CARD_LABELS.ratingSize}>
            <InputNumber
              size="small"
              min={8}
              max={64}
              disabled={locked}
              style={{ width: NUMBER_WIDTH }}
              value={infoCardRatingSizePx(style.ratingSize)}
              onChange={(px) => setProp('ratingSize', pxToRemCss(px))}
            />
          </LabelledRow>
        </>
      )}
      <LabelledRow label={INFO_CARD_LABELS.badges} stacked>
        <BadgesField
          badges={badges}
          disabled={locked}
          labelOfColor={labelOfColor}
          colorOfLabel={colorOfLabel}
          onChange={(next) => setProp('badges', next)}
        />
      </LabelledRow>

      <FieldGroupLabel>{INFO_CARD_GROUPS.card}</FieldGroupLabel>
      <LabelledRow label={INFO_CARD_LABELS.cardLayout}>
        <Select
          size="small"
          disabled={locked}
          value={style.cardLayout}
          options={[
            { value: 'vertical', label: 'Verticale' },
            { value: 'horizontal', label: 'Horizontale' },
          ]}
          onChange={(value) => setProp('cardLayout', value)}
          style={{ width: SELECT_WIDTH }}
        />
      </LabelledRow>
      <LabelledRow label={INFO_CARD_LABELS.contentAlign}>
        <Select
          size="small"
          disabled={locked}
          value={style.contentAlign}
          options={CONTENT_ALIGN_OPTIONS}
          onChange={(value) => setProp('contentAlign', value)}
          style={{ width: SELECT_WIDTH }}
        />
      </LabelledRow>
      <LabelledRow label={INFO_CARD_LABELS.lineGap}>
        <InputNumber
          size="small"
          min={0}
          max={48}
          disabled={locked}
          style={{ width: NUMBER_WIDTH }}
          value={cssLengthToPx(style.lineGap) ?? 10}
          onChange={(px) => setProp('lineGap', pxToRemCss(px))}
        />
      </LabelledRow>
      <LabelledRow label={INFO_CARD_LABELS.backgroundColor}>
        <BoxColorField
          label="Couleur de fond de la carte"
          disabled={locked}
          value={labelOfColor(style.backgroundColor)}
          onChange={(label) => setProp('backgroundColor', colorOfLabel(label))}
        />
      </LabelledRow>

      <LabelledRow label={SHADOW_LABEL}>
        <Select
          size="small"
          disabled={locked}
          value={style.shadow ?? 'none'}
          options={SHADOW_OPTIONS}
          onChange={(value) => setProp('shadow', value)}
          style={{ width: SELECT_WIDTH }}
        />
      </LabelledRow>

      <BoxBorderFields
        border={style.border}
        locked={locked}
        labelOfColor={labelOfColor}
        colorOfLabel={colorOfLabel}
        onChange={(border) => setProp('border', border)}
      />
    </div>
  );
};

export default InfoCardFields;
